/**
 * Filename: postal-directory.ts
 * Description: Loads and searches the complete Japan Post directory bundled with the admin application.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-08-27
 * Updated: 2026-08-27
 */

export interface PostalChunkRecord {
  prefJp: string;
  cityJp: string;
  streetJp: string;
  prefEn?: string;
  cityEn?: string;
  streetEn?: string;
}

export interface PostalDirectoryEntry {
  code: string;
  zip: string;
  prefectureJp: string;
  cityJp: string;
  streetJp: string;
  prefectureEn: string;
  cityEn: string;
  streetEn: string;
  searchText: string;
}

interface PostalDirectoryManifest {
  version: number;
  totalEntries: number;
  parts: Array<{ filename: string; count: number }>;
}

let cachedDirectoryPromise: Promise<PostalDirectoryEntry[]> | undefined;

export function normalizePostalSearchValue(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase('en')
    .replace(/[\s,，、.・'’\-/]/gu, '');
}

export function createPostalDirectoryEntry(
  zip: string,
  record: PostalChunkRecord
): PostalDirectoryEntry {
  const code = `${zip.slice(0, 3)}-${zip.slice(3)}`;
  const fields = [
    zip,
    code,
    record.prefJp,
    record.cityJp,
    record.streetJp,
    record.prefEn ?? '',
    record.cityEn ?? '',
    record.streetEn ?? '',
  ];
  return {
    code,
    zip,
    prefectureJp: record.prefJp,
    cityJp: record.cityJp,
    streetJp: record.streetJp,
    prefectureEn: record.prefEn ?? '',
    cityEn: record.cityEn ?? '',
    streetEn: record.streetEn ?? '',
    searchText: normalizePostalSearchValue(fields.join(' ')),
  };
}

export function searchPostalDirectory(
  entries: PostalDirectoryEntry[],
  rawSearchTerm: string
): PostalDirectoryEntry[] {
  const searchTerm = normalizePostalSearchValue(rawSearchTerm);
  if (!searchTerm) return entries;
  return entries.filter((entry) => entry.searchText.includes(searchTerm));
}

export async function loadPostalDirectory(): Promise<PostalDirectoryEntry[]> {
  if (cachedDirectoryPromise) return cachedDirectoryPromise;
  const publicUrl = process.env.PUBLIC_URL ?? '';
  cachedDirectoryPromise = fetch(`${publicUrl}/postal/directory-manifest.json`, {
    headers: { Accept: 'application/json' },
  })
    .then(async (response) => {
      const contentType = response.headers.get('content-type') ?? '';
      if (!response.ok || !contentType.includes('application/json')) {
        throw new Error('The postal directory is unavailable in this admin deployment.');
      }
      const manifest = (await response.json()) as PostalDirectoryManifest;
      if (manifest.version !== 1 || !Array.isArray(manifest.parts) || !manifest.parts.length) {
        throw new Error('The postal directory format is not supported.');
      }
      const parts = await Promise.all(
        manifest.parts.map(async (part) => {
          if (!/^directory-[0-9]\.json$/u.test(part.filename)) {
            throw new Error('The postal directory manifest contains an invalid filename.');
          }
          const partResponse = await fetch(`${publicUrl}/postal/${part.filename}`, {
            headers: { Accept: 'application/json' },
          });
          const partContentType = partResponse.headers.get('content-type') ?? '';
          if (!partResponse.ok || !partContentType.includes('application/json')) {
            throw new Error(`Postal directory partition ${part.filename} is unavailable.`);
          }
          const records = (await partResponse.json()) as Record<string, PostalChunkRecord>;
          if (Object.keys(records).length !== part.count) {
            throw new Error(`Postal directory partition ${part.filename} is incomplete.`);
          }
          return records;
        })
      );
      const records = Object.assign({}, ...parts) as Record<string, PostalChunkRecord>;
      if (Object.keys(records).length !== manifest.totalEntries) {
        throw new Error('The postal directory is incomplete.');
      }
      return Object.entries(records)
        .map(([zip, record]) => createPostalDirectoryEntry(zip, record))
        .sort((left, right) => left.zip.localeCompare(right.zip));
    })
    .catch((error) => {
      cachedDirectoryPromise = undefined;
      throw error;
    });
  return cachedDirectoryPromise;
}
